import {getCopilotSpacePath, SLUG_ERROR_MESSAGE} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import type {CopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useAutoSpaceName} from '../hooks'
import type {CopilotSpacesConfigPayload, CopilotSpaceRepositoryBackedResource} from '../types'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {resourcesForDuplication} from '../utils/resources-for-duplication'
import {useNavigate} from '@github-ui/use-navigate'
import {Dialog, FormControl, Stack, Textarea, TextInput, useConfirm} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'

import {useUpsertCopilotSpace} from '../hooks/use-upsert-copilot-space'
import {type OwnerItem, SpacesOwnerDropdown} from './SpacesOwnerDropdown'
import styles from './CreateSpaceDialog.module.css'
import {SpaceDialogFooter} from './SpaceDialogFooter'
import {sendEvent} from '@github-ui/hydro-analytics'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'

export function CreateSpaceDialog({
  dialogTitle = 'New Space',
  closeDialog,
  initialSpace,
}: {
  dialogTitle?: string
  closeDialog: () => void
  initialSpace?: CopilotSpace
}) {
  const navigate = useNavigate()

  const nameInputRef = useRef<HTMLInputElement>(null)
  const defaultName = useAutoSpaceName()
  const [name, setName] = useState(initialSpace?.name ?? defaultName)
  const [description, setDescription] = useState(initialSpace?.description ?? '')
  const [owner, setOwner] = useState<OwnerItem | null>(null)
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
  const {upsertCopilotSpace, isPending: isSaving} = useUpsertCopilotSpace()
  const confirm = useConfirm()

  const {
    copilotSpacesConfig: {maxDescriptionLength},
  } = useAppPayload<CopilotSpacesConfigPayload>()

  const clearErrors = useCallback(
    (fields: string[]) => {
      const updatedErrorMessages = {...errorMessages}
      for (const field of fields) {
        if (updatedErrorMessages[field]) {
          delete updatedErrorMessages[field]
        }
      }
      setErrorMessages(updatedErrorMessages)
    },
    [errorMessages],
  )
  const dropdownOnSelect = useCallback((newOwner: OwnerItem) => setOwner(newOwner), [])

  // Track when the v3 create dialog was opened: https://github.com/github/copilot-experiences/issues/6823
  useEffect(() => {
    sendEvent('copilot_spaces.create_space_v3_dialog_opened', {})
  }, [])

  useEffect(() => {
    if (initialSpace) {
      // If an initial space is being provided we want to focus instead of highlight since the use may not want to
      // change the whole name. They may want to leave it and just set a different owner, for example.
      nameInputRef.current?.focus()
    } else {
      // If the initialSpace is not provided that means we use an auto-generated name. We want to select the input so
      // that the user can quickly set their own name
      nameInputRef.current?.select()
    }
  }, [initialSpace])

  const saveSpace = async (): Promise<void> => {
    if (isSaving) return

    let resourcesToSave = initialSpace?.resources ? [...initialSpace.resources] : []
    let resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[] = []

    if (copilotFeatureFlags.spacesDuplicate) {
      if (initialSpace && owner) {
        const duplicationResult = resourcesForDuplication(
          initialSpace.resources || [],
          owner.id,
          owner.type === 'Organization',
        )
        resourcesToSave = duplicationResult.resourcesToSave
        resourcesThatWillNotTransfer = duplicationResult.resourcesThatWillNotTransfer
      }

      // Treat the save as confirmed unless some resource will not transfer
      let confirmed = true
      if (resourcesThatWillNotTransfer.length > 0) {
        // Returns a comma separated list of owner logins from resources that will not transfer. E.g. "github, vercel, and primer"
        const ownerLoginsAsSentence = new Intl.ListFormat().format(
          // Make it a Set first so that owner logins are unique. Then convert back to Array for formatting
          Array.from(
            new Set(
              resourcesThatWillNotTransfer
                .map(r => r.ownerLogin)
                .filter((login): login is string => login !== undefined),
            ),
          ),
        )
        const content = `Private resources owned by <strong>${ownerLoginsAsSentence}</strong> will not be duplicated to the new space.`
        confirmed = await confirm({
          title: 'Some resources will not be duplicated',
          // Allows the <strong> tags to be rendered as HTML but will still escape unsafe HTML if present.
          content: <UnsafeHTMLText html={content} />,
          cancelButtonContent: 'Never mind',
          confirmButtonContent: 'Continue',
          confirmButtonType: 'primary',
        })
      }

      if (!confirmed) return
    }

    try {
      const newSpace = await upsertCopilotSpace({
        name,
        description,
        ownerId: owner?.id,
        ownerType: owner?.type,
        resources: resourcesToSave,
        generalInstructions: initialSpace?.generalInstructions,
      })

      setErrorMessages({})
      // First close the dialog to prevent conditions where we navigate before the dialog is closed and the dialog pops
      // open again when going back to the space's page.
      closeDialog()
      // navigate to the newly created space
      const path = getCopilotSpacePath(newSpace)
      return navigate(path)
    } catch (errors) {
      const errs = errors ?? {}
      setErrorMessages(errs as Record<string, string>)
    }
  }
  const handleCancel = () => {
    closeDialog()
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      title={dialogTitle}
      onClose={closeDialog}
      width="large"
      aria-label={dialogTitle}
      className={styles.createSpaceDialog}
      renderFooter={() => (
        <SpaceDialogFooter onCancel={handleCancel} onSave={saveSpace} isLoading={isSaving} disabled={!owner} />
      )}
    >
      <form
        onSubmit={async e => {
          e.preventDefault()
          e.stopPropagation()
          await saveSpace()
        }}
      >
        <Stack>
          <FormControl>
            <FormControl.Label required>Name</FormControl.Label>
            <TextInput
              ref={nameInputRef}
              autoFocus
              block
              data-testid="copilot-space-name-input"
              name="copilot-space-name"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors(['name', 'slug'])
              }}
            />
            {errorMessages.name ? (
              <FormControl.Validation variant="error">
                <span>Name {errorMessages.name}</span>
              </FormControl.Validation>
            ) : errorMessages.slug ? (
              <FormControl.Validation variant="error">
                <span>{SLUG_ERROR_MESSAGE}</span>
              </FormControl.Validation>
            ) : null}
          </FormControl>
          <SpacesOwnerDropdown onSelect={dropdownOnSelect} required showCaption={false} />
          <FormControl>
            <FormControl.Label required={false}>Description</FormControl.Label>
            <Textarea
              block
              name="description"
              value={description}
              rows={3}
              resize="vertical"
              className={styles.textAreaWrapper}
              onChange={e => {
                setDescription(e.target.value)
                clearErrors(['description'])
              }}
            />
            {errorMessages.description ? (
              <FormControl.Validation variant="error">
                <span>Description {errorMessages.description}</span>
              </FormControl.Validation>
            ) : (
              <CharacterCount text={description} promptCharLimit={maxDescriptionLength} />
            )}
          </FormControl>
        </Stack>
      </form>
    </Dialog>
  )
}

function CharacterCount({text, promptCharLimit}: {text: string; promptCharLimit: number}) {
  const tooLong = text.length > promptCharLimit
  const currentCount = (
    <>
      {text.length.toLocaleString()} / {promptCharLimit.toLocaleString()} characters
    </>
  )
  return (
    <>
      {tooLong ? (
        <FormControl.Validation variant="error">{currentCount}</FormControl.Validation>
      ) : (
        // This is a direct child, it just doesn't know that because it is a fragment within a function call
        // eslint-disable-next-line primer-react/direct-slot-children
        <FormControl.Caption>{currentCount}</FormControl.Caption>
      )}
    </>
  )
}

try{ CreateSpaceDialog.displayName ||= 'CreateSpaceDialog' } catch {}
try{ CharacterCount.displayName ||= 'CharacterCount' } catch {}